import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { Download } from 'react-bootstrap-icons';
import { adminApi } from '../../services/adminApi';
import templateService, { TemplateCategory } from '../../services/templateService';

interface Template {
  id: string;
  title: string;
  description?: string;
  instructions?: string;
  helperPrompt?: string;
  categoryId: string;
  icon?: string;
  order?: number;
  isActive?: boolean;
  createdAt?: any;
  updatedAt?: any;
}

const TemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    helperPrompt: '',
    categoryId: '',
    icon: '',
    order: 0,
    isActive: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const categoryData = await templateService.getCategories();
      setCategories(categoryData);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await adminApi.getTemplates();
      setTemplates(response.data);
    } catch (err: any) {
      setError('Failed to fetch templates');
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template: Template) => {
    setModalMode('edit');
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      description: template.description || '',
      instructions: template.instructions || '',
      helperPrompt: template.helperPrompt || '',
      categoryId: template.categoryId || '',
      icon: template.icon || '',
      order: template.order || 0,
      isActive: template.isActive !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (modalMode === 'create') {
        await adminApi.createTemplate(formData);
      } else if (selectedTemplate) {
        await adminApi.updateTemplate(selectedTemplate.id, formData);
      }
      await fetchTemplates();
      setShowModal(false);
      setSelectedTemplate(null);
    } catch (err: any) {
      alert('Failed to save template');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDelete = (template: Template) => {
    setSelectedTemplate(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await adminApi.deleteTemplate(selectedTemplate.id);
      await fetchTemplates();
      setShowDeleteModal(false);
      setSelectedTemplate(null);
    } catch (err: any) {
      alert('Failed to delete template');
    }
  };

  const handleExportTemplates = () => {
    try {
      const exportData = templates.map(template => ({
        id: template.id,
        title: template.title,
        description: template.description,
        instructions: template.instructions,
        helperPrompt: template.helperPrompt,
        categoryId: template.categoryId,
        icon: template.icon,
        order: template.order,
        isActive: template.isActive
      }));

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `templates-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting templates:', err);
      alert('Failed to export templates');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleExportTemplates}
          disabled={templates.length === 0}
        >
          <Download className="me-2" />
          Export JSON
        </Button>
      </div>
      <Table responsive striped bordered hover className="admin-template-table">
        <thead>
          <tr>
            <th>Title</th>
            <th className="description-col">Description</th>
            <th>Instructions</th>
            <th>Category</th>
            <th className="actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => (
            <tr key={template.id}>
              <td className="admin-table-cell-small">{template.title}</td>
              <td className="admin-table-cell-small">{template.description || '-'}</td>
              <td className="admin-table-cell-small">
                {template.instructions ? (
                  <span className="admin-template-instructions">
                    {template.instructions}
                  </span>
                ) : '-'}
              </td>
              <td className="admin-table-cell-small">{categories.find(c => c.id === template.categoryId)?.name || template.categoryId || '-'}</td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleEdit(template)}
                >
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" dialogClassName="admin-edit-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Create New Template' : 'Edit Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="Enter instructions for using this template..."
                className="admin-modal-textarea-instructions"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Helper Prompt</Form.Label>
              <Form.Control
                as="textarea"
                rows={18}
                value={formData.helperPrompt}
                onChange={(e) => setFormData({ ...formData, helperPrompt: e.target.value })}
                placeholder="Enter the helper prompt that will be shown to users when they click this template..."
                className="admin-modal-textarea-helper"
              />
              <Form.Text className="text-muted">
                This is the initial message that appears from Claude when a user selects this template.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="admin-modal-footer">
          <Button variant="link" onClick={() => setShowModal(false)} className="admin-modal-btn">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!formData.title}
            className="admin-modal-btn"
          >
            {modalMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete template "{selectedTemplate?.title}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete Template
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TemplateManagement;